import { z } from "zod";

import {
  IsoTimestampSchema,
  LocaleSchema,
  LocalizedTextSchema,
  MoneyHalalasSchema,
  UploadIdSchema,
} from "./common";

/**
 * The **shopper's** reason vocabulary: the codes a shopper picks from a list in the portal.
 *
 * This enum is **closed on purpose**, and extending it is a deliberate change, not a
 * configuration knob. What a merchant configures in `policy.reasons[]` is which of these
 * codes are `enabled`, their `photoRequired` flag, and their bilingual `label` — the
 * merchant supplies the *wording*, never a new code. That is why `spoiled` had to be added
 * here for `qut-foods`: a genuinely new shopper-facing reason is a product decision (it
 * needs an `ar`/`en` catalogue entry, may need a fee rule, and shows up in
 * `autoApproveRules.reasonCodes`), so it is reviewed, not merchant-authored.
 */
export const ReturnReasonCodeSchema = z.enum([
  "size",
  "damaged",
  "notlike",
  "changed",
  "wrong",
  // Perishables arrive spoiled without being "damaged" in the handling sense, and a food
  // merchant cannot express that with the other five. See fixtures/merchants.json (qut-foods).
  "spoiled",
  // The one reason that REQUIRES the shopper's own words. None of the codes above fit,
  // so the line's `note` must be non-empty (policy validation `note_required`) and the
  // merchant decides with that text in front of them. It is still a closed code: the
  // merchant relabels and toggles it like any other.
  "other",
]);
export type ReturnReasonCode = z.infer<typeof ReturnReasonCodeSchema>;

/**
 * The **system's** reason vocabulary: why a return exists when nobody asked for one.
 *
 * A shopper never picks one of these from a list — the event happened *to* the parcel.
 * Keeping them out of `ReturnReasonCodeSchema` is the point: `ReasonConfigSchema.code` is
 * the shopper enum, so policy check 6 (`reasons[].enabled`) can never offer a system code
 * in the shopper's reason picker, and a submitted system code fails check 6 as
 * `reason_not_offered`.
 *
 * Used by the failed-delivery / refused-COD entry points (docs/04 "Entry points"), which
 * open a return directly at `in_transit` or `received`.
 */
export const SystemReasonCodeSchema = z.enum([
  "cod_refused",
  "delivery_failed",
]);
export type SystemReasonCode = z.infer<typeof SystemReasonCodeSchema>;

/**
 * Everything a `return_line.reason_code` may hold: a shopper's choice or a system-originated
 * cause. Two vocabularies, one column — never conflated, because the tracking page must not
 * render a sweep as though the shopper had chosen it.
 */
export const LineReasonCodeSchema = z.enum([
  ...ReturnReasonCodeSchema.options,
  ...SystemReasonCodeSchema.options,
] as [
  ReturnReasonCode | SystemReasonCode,
  ...Array<ReturnReasonCode | SystemReasonCode>,
]);
export type LineReasonCode = z.infer<typeof LineReasonCodeSchema>;

const SYSTEM_REASON_CODES: ReadonlySet<string> = new Set<string>(
  SystemReasonCodeSchema.options,
);

/** Did the system open this line, rather than the shopper choosing a reason for it? */
export function isSystemReasonCode(code: string): code is SystemReasonCode {
  return SYSTEM_REASON_CODES.has(code);
}

/** docs/03 — `return_order.resolution_type`. The shopper's choice is a REQUEST, not an execution (ADR-003). */
export const ResolutionTypeSchema = z.enum(["refund", "exchange", "credit"]);
export type ResolutionType = z.infer<typeof ResolutionTypeSchema>;

/** docs/03 — `return_order.collection_method`. */
export const CollectionMethodSchema = z.enum(["pickup", "dropoff"]);
export type CollectionMethod = z.infer<typeof CollectionMethodSchema>;

/** docs/05 — `FeeConfig`. */
export const FeeTypeSchema = z.enum(["flat", "percent"]);
export type FeeType = z.infer<typeof FeeTypeSchema>;

export const FeeConfigSchema = z
  .object({
    type: FeeTypeSchema,
    /**
     * `flat`: integer halalas.
     * `percent`: integer basis-free percentage points (0-100) of unit_price x qty_expected.
     * `0` means no fee — never represent "no fee" as an absent key in one place and a zero
     * in another; normalise on read (docs/05).
     */
    amount: z.number().int().nonnegative(),
  })
  .refine((fee) => fee.type !== "percent" || fee.amount <= 100, {
    message: "a percent fee cannot exceed 100",
    path: ["amount"],
  });
export type FeeConfig = z.infer<typeof FeeConfigSchema>;

/**
 * docs/08 step 3 — "Sub-reasons appear only where configured".
 *
 * The `code` is a free string, not an enum: unlike `ReturnReasonCode` (a closed, reviewed
 * shopper vocabulary), a sub-reason is merchant-authored detail under a reason the merchant
 * already offers — "too small" / "too large" under `size`. It never widens what may be
 * returned, it only sharpens why, and it lands on `return_line.sub_reason`, which is
 * already `z.string().nullable()`. Making it an enum would mean a code review every time a
 * merchant wanted a fourth bullet under a reason they already have.
 *
 * The label is bilingual with both halves required, like every other user-facing string.
 */
export const SubReasonConfigSchema = z.object({
  code: z.string().min(1),
  label: LocalizedTextSchema,
});
export type SubReasonConfig = z.infer<typeof SubReasonConfigSchema>;

/** docs/03 — `merchant_policy.reasons` is `ReasonConfig[]`. */
export const ReasonConfigSchema = z.object({
  /**
   * Shopper vocabulary only. A merchant toggles and re-labels these codes; it cannot
   * introduce one, and a `SystemReasonCode` can never appear here — which is what keeps
   * `cod_refused` out of the shopper's reason picker (policy check 6).
   */
  code: ReturnReasonCodeSchema,
  label: LocalizedTextSchema,
  enabled: z.boolean(),
  photoRequired: z.boolean(),
  /**
   * Optional, and an ABSENT list is the meaningful case: docs/08 step 3 shows the
   * sub-reason control "only where configured", so absent and `[]` both mean "render no
   * sub-reason picker for this reason". A shopper is never forced to pick one.
   */
  subReasons: z.array(SubReasonConfigSchema).optional(),
});
export type ReasonConfig = z.infer<typeof ReasonConfigSchema>;

export const ResolutionsConfigSchema = z.object({
  refund: z.boolean(),
  exchange: z.boolean(),
  credit: z.boolean(),
});
export type ResolutionsConfig = z.infer<typeof ResolutionsConfigSchema>;

/** docs/05 — evaluated once, on creation. Permissive-on-match; there is no auto-reject. */
export const AutoApproveRuleSchema = z.object({
  /** Order value ceiling, integer halalas. */
  maxOrderValue: MoneyHalalasSchema.optional(),
  maxItemCount: z.number().int().positive().optional(),
  reasonCodes: z.array(ReturnReasonCodeSchema).optional(),
  resolutions: z.array(ResolutionTypeSchema).optional(),
});
export type AutoApproveRule = z.infer<typeof AutoApproveRuleSchema>;

/**
 * `#rgb` or `#rrggbb` only. The value is written into a CSS custom property on the shopper
 * page, so anything looser is an injection surface, not a colour.
 */
export const HexColorSchema = z
  .string()
  .trim()
  .regex(
    /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i,
    "must be a hex colour like #039BA0",
  );

export const MerchantThemeSchema = z.object({
  brandColor: HexColorSchema.nullable().optional(),
  /**
   * Where the logo the shopper sees comes from. `logoUploadId` is a logo the merchant
   * uploaded through their settings and wins whenever it is set; `logoUrl` is the store
   * avatar the platform gave us at install, and is what a merchant who never touches this
   * still gets. The API collapses the two into one absolute URL before a shopper's
   * browser ever sees the theme (`services/merchant-logo.ts`).
   */
  logoUrl: z.string().url().nullable().optional(),
  logoUploadId: UploadIdSchema.nullable().optional(),
  displayName: LocalizedTextSchema.optional(),
  /**
   * The page background, independent of `brandColor` (which stays scoped to the primary
   * action). `backgroundGradientTo` alone (no `backgroundColor`) forms a two-stop gradient
   * when both are present; `backgroundColor` alone is a valid solid background.
   */
  backgroundColor: HexColorSchema.nullable().optional(),
  backgroundGradientTo: HexColorSchema.nullable().optional(),
});
export type MerchantTheme = z.infer<typeof MerchantThemeSchema>;

/**
 * docs/03 `merchant_policy` — the mutable, merchant-editable policy.
 * Copied verbatim onto `return_order.policy_snapshot` at creation (ADR-006).
 */
export const MerchantPolicySchema = z.object({
  enabled: z.boolean(),
  windowDays: z.number().int().nonnegative(),
  // One config per code: a duplicate would make `fees[code]` and the settings form's
  // per-code row ambiguous.
  reasons: z
    .array(ReasonConfigSchema)
    .refine(
      (reasons) => new Set(reasons.map((r) => r.code)).size === reasons.length,
      { message: "each reason code may be configured once" },
    ),
  resolutions: ResolutionsConfigSchema,
  /**
   * Keyed by reason code. Missing key === no fee (normalise on read, docs/05).
   * Typed as a partial map, because most merchants configure a fee for one reason only.
   */
  fees: z.record(z.string(), FeeConfigSchema),
  excludedSkus: z.array(z.string()),
  blockedPhones: z.array(z.string()),
  dropoffAllowed: z.boolean(),
  autoApproveRules: z.array(AutoApproveRuleSchema),
  theme: MerchantThemeSchema.optional(),
  localeDefault: LocaleSchema.optional(),
  updatedAt: IsoTimestampSchema.optional(),
});
export type MerchantPolicy = z.infer<typeof MerchantPolicySchema>;

/**
 * TODO(decision):Q1 — Legal minimum return window and the set of reasons a merchant
 * may not disable are not settled. Do NOT hardcode a number here. The floor is supplied
 * to the policy engine as config so both options stay open; this schema only describes
 * its shape, and `nonDisableableReasons` is deliberately left empty by default.
 */
export const LegalFloorConfigSchema = z.object({
  minWindowDays: z.number().int().nonnegative(),
  nonDisableableReasons: z.array(ReturnReasonCodeSchema).default([]),
});
export type LegalFloorConfig = z.infer<typeof LegalFloorConfigSchema>;

/**
 * The snapshot frozen onto a return at creation. Structurally the merchant policy plus
 * the legal floor that applied and the moment it was taken. Never re-read live policy
 * for an existing return (ADR-006).
 */
export const PolicySnapshotSchema = z.object({
  policy: MerchantPolicySchema,
  legalFloor: LegalFloorConfigSchema.optional(),
  snapshotAt: IsoTimestampSchema,
});
export type PolicySnapshot = z.infer<typeof PolicySnapshotSchema>;

/** What the shopper portal is allowed to see of a policy. No blocked phones, no auto-approve rules. */
export const PublicPolicySchema = z.object({
  windowDays: z.number().int().nonnegative(),
  reasons: z.array(
    // `subReasons` is projected through: docs/08 step 3 renders the sub-reason picker from
    // the policy response, never from anything hardcoded in the portal.
    ReasonConfigSchema.pick({
      code: true,
      label: true,
      photoRequired: true,
      subReasons: true,
    }),
  ),
  resolutions: ResolutionsConfigSchema,
  /**
   * Policy check 9 — the fee table the portal needs to make the disclosure, keyed by reason
   * code exactly as `MerchantPolicy.fees` is.
   *
   * Without this the portal could only ever render "no return fee", which is a FALSE
   * statement for any merchant who configures one (the `orla-home` fixture charges a
   * 1500-halala flat fee on `changed`), and a surprise deduction at refund time is the
   * product failure this whole surface exists to prevent. docs/05 check 9 requires the fee
   * be shown before submit and again at review, so the shopper must be able to compute it
   * for their chosen reason and quantity before they commit.
   *
   * This carries the merchant's CONFIG, not a computed figure, because the fee depends on
   * the reason and quantity the shopper has not chosen yet. Apply it with `feeForReason()`
   * and `computeLineFee()` from `@sirdab/returns-core` — flat is charged once per line regardless
   * of quantity, percent is of `unitPrice x qty` rounded to the nearest halala, and absent
   * or zero both mean no fee (docs/05 "Fees"). Do not reimplement that arithmetic.
   *
   * Optional so an API that does not yet project it stays valid; an absent table means the
   * portal discloses no fee, which is the honest reading of a merchant who configured none.
   * The client's figure is a disclosure, never a control: `fee_applied` is resolved
   * server-side from the snapshot at creation and frozen there (ADR-006).
   *
   * Safe to expose: a fee is a term of the return the shopper is about to accept. It leaks
   * nothing the shopper is not entitled to know, unlike `blockedPhones` or
   * `autoApproveRules`, which stay off this schema.
   */
  fees: z.record(z.string(), FeeConfigSchema).optional(),
  dropoffAllowed: z.boolean(),
  theme: MerchantThemeSchema.optional(),
  localeDefault: LocaleSchema.optional(),
});
export type PublicPolicy = z.infer<typeof PublicPolicySchema>;

/** docs/05 — per-line eligibility. Ineligible lines are shown, never filtered out (ADR-007). */
export const IneligibilityReasonCodeSchema = z.enum([
  "window_closed",
  "sku_excluded",
  "already_returned",
  "not_delivered",
]);
export type IneligibilityReasonCode = z.infer<
  typeof IneligibilityReasonCodeSchema
>;

export const LineEligibilitySchema = z.object({
  lineRef: z.string().min(1),
  eligible: z.boolean(),
  reasonCode: IneligibilityReasonCodeSchema.optional(),
  reasonText: LocalizedTextSchema,
});
export type LineEligibility = z.infer<typeof LineEligibilitySchema>;

/** A policy failure surfaced on submit (`422 policy_violation`). */
export const PolicyViolationSchema = z.object({
  /**
   * Absent for an order-level problem (no lines, no resolution offered). The Rails pack
   * sends `null` there, read here as absent so the whole 422 still parses.
   */
  lineRef: z.preprocess(
    (value) => (value === null ? undefined : value),
    z.string().min(1).optional(),
  ),
  code: z.string().min(1),
  reasonText: LocalizedTextSchema,
});
export type PolicyViolation = z.infer<typeof PolicyViolationSchema>;
